// Pure helpers behind RunDetail's Transcript + Facts tabs (unit-tested in transcriptModel.test.js).
import { matchAnswerToOptions } from "./components/TranscriptMCQ";

// Pair each system turn with the NEXT patient turn. When the system turn carries `mcq_options` and the
// patient reply matches ≥1 option, the patient turn gets `mcqView = {options, selection, selectedIds}`
// (render the read-only MCQ in place of the reply text). Otherwise `mcqView` is null → render as plain
// text — this covers free-text questions, unmatched replies, and old runs stored before `mcq_options`.
export function buildTranscriptView(transcript) {
  const tr = transcript || [];
  return tr.map((t, i) => {
    const prev = i > 0 ? tr[i - 1] : null;
    if (t.role !== "patient" || !prev || prev.role !== "system" || !Array.isArray(prev.mcq_options)) {
      return { ...t, mcqView: null };
    }
    const selectedIds = matchAnswerToOptions(prev.mcq_options, prev.mcq_selection, t.text);
    return { ...t, mcqView: selectedIds.length
      ? { options: prev.mcq_options, selection: prev.mcq_selection, selectedIds } : null };
  });
}

// Question → answer pairs for the transcript tab: each system question with the patient reply that
// follows it (reply carries `mcqView` from buildTranscriptView). The final question (e.g. Mari's summary)
// has answer=null; a patient turn with no preceding question gets question=null. Each pair carries the
// transcript indices of its question/answer (`qIndex`/`aIndex`, null when absent) so the Root cause tab
// can jump to a question by its transcript index (rootcauseModel `qaPairIndex`).
export function buildQAPairs(transcript) {
  const pairs = [];
  buildTranscriptView(transcript).forEach((t, i) => {
    const last = pairs[pairs.length - 1];
    if (t.role === "system") pairs.push({ question: t, answer: null, qIndex: i, aIndex: null });
    else if (last && !last.answer) { last.answer = t; last.aIndex = i; }
    else pairs.push({ question: null, answer: t, qIndex: null, aIndex: i });
  });
  return pairs;
}

export const selectedLabels = (mcqView) => (mcqView
  ? mcqView.options.filter((o) => mcqView.selectedIds.includes(o.id)).map((o) => o.label) : []);

// Plain-text transcript for the Copy button. Matched MCQ replies list the selected option labels.
export const copyTranscript = (transcript) => buildTranscriptView(transcript).map((t) => {
  if (t.role === "system") return `Assistant: ${t.text}`;
  if (t.mcqView) return `Patient: [selected] ${selectedLabels(t.mcqView).join("; ")}  (reply: ${t.text})`;
  return `Patient: ${t.text}`;
}).join("\n\n");

// Question resolver for captured facts. New runs carry the server-stamped `fact.question`. Old runs
// (before 2026-10-20) derive it from the transcript: the server's loop appends opening system turn at
// index 0, then per turn N a patient reply (2N-1) + the next system question (2N), so facts with
// `turn` N answer the system turn at index 2(N-1). This holds ONLY while the transcript strictly
// alternates system/patient up to that point (a mid-interview empty question would skip a system
// turn), so we verify alternation AND that the system turn's node_id equals the fact's node_id.
// Any doubt → null (the caller shows the node label only; never a wrong question).
export function makeQuestionResolver(transcript) {
  const tr = transcript || [];
  let alt = 0;   // number of leading turns that alternate system, patient, system, …
  while (alt < tr.length && tr[alt]?.role === (alt % 2 ? "patient" : "system")) alt++;
  return (fact) => {
    if (typeof fact?.question === "string" && fact.question) return fact.question;
    const n = fact?.turn;
    if (!Number.isInteger(n) || n < 1) return null;
    const qi = 2 * (n - 1);
    if (qi + 1 >= alt) return null;          // question + its reply must both sit in the verified prefix
    const q = tr[qi];
    if ((q.node_id ?? null) !== (fact.node_id ?? null)) return null;
    return q.text || null;
  };
}

export const isPresetFact = (f) => (f.key || "").startsWith("preset.");
export const nodeLabel = (nid) => (nid ? `node ${nid}` : "intake");

// Group consecutive (chronological) facts by (question, node_id).
export function groupFacts(facts, transcript) {
  const resolve = makeQuestionResolver(transcript);
  const groups = [];
  for (const f of facts || []) {
    const nid = f.node_id ?? null;
    const question = resolve(f);
    const last = groups[groups.length - 1];
    if (last && last.node_id === nid && last.question === question) last.facts.push(f);
    else groups.push({ node_id: nid, question, facts: [f] });
  }
  return groups;
}

export const fmtFactValue = (v) => {
  if (v === true) return "yes";
  if (v === false) return "no";
  if (v == null) return "—";
  if (Array.isArray(v)) return v.map(fmtFactValue).join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

// Plain-text facts for the Copy button: same layout as the tab (question + node label header,
// raw dot-notation keys), preset/config facts last.
export function copyFacts(facts, transcript) {
  const all = facts || [];
  const out = [];
  for (const g of groupFacts(all.filter((f) => !isPresetFact(f)), transcript)) {
    if (out.length) out.push("");
    out.push(g.question ? `${g.question}  [${nodeLabel(g.node_id)}]` : `[${nodeLabel(g.node_id)}]`);
    for (const f of g.facts) {
      out.push(`  ${f.key}: ${fmtFactValue(f.value)}${f.changed ? ` (was: ${fmtFactValue(f.prev_value)})` : ""}`);
    }
  }
  const preset = all.filter(isPresetFact);
  if (preset.length) {
    if (out.length) out.push("");
    out.push("Config / preset facts");
    for (const f of preset) out.push(`  ${f.key}: ${fmtFactValue(f.value)}`);
  }
  return out.join("\n");
}
